/**
 * Captura best-effort de la ubicación del dispositivo para el GEO-SELLO de una
 * foto de evidencia (proof-of-presence).
 *
 * Reglas de diseño:
 * · NUNCA bloquea la subida: si el permiso está denegado, no hay GPS, o expira
 *   el timeout, resuelve a `null` y la foto se sube sin sello (columnas NULL).
 * · No lanza: cualquier error se traga y se devuelve `null`.
 * · Devuelve solo lo que persiste el dominio (lat/lng/accuracy); el resto del
 *   `GeolocationPosition` se descarta.
 * · SIN CACHÉ de posición (`maximumAge: 0`). Es la regla que hace honesto al
 *   geo-sello, y no es cosmética: con caché (antes 30 s) el navegador sirve la
 *   última posición conocida SIN volver a consultar el permiso, así que
 *   —medido en e2e/geo-stamp— una foto tomada tras REVOCAR el permiso de
 *   ubicación seguía saliendo sellada con la coordenada anterior. Dos defectos
 *   en uno para un producto de cumplimiento: (1) se adjunta ubicación después
 *   de retirar el consentimiento, y (2) el sello dice «aquí se tomó esta foto»
 *   cuando puede ser una posición de hace medio minuto. La captura corre en
 *   paralelo con la subida (ver photo-strip) y nunca la bloquea, así que pedir
 *   una posición fresca no penaliza la evidencia: como mucho no hay sello.
 */

export interface CapturedGeo {
  latitude: number;
  longitude: number;
  /** Radio de precisión en metros (o null si el dispositivo no lo reporta). */
  geoAccuracy: number | null;
}

const DEFAULT_TIMEOUT_MS = 8000;

/**
 * Antigüedad máxima aceptada para la posición. 0 = siempre una lectura nueva
 * (ver la nota de cabecera: el caché del navegador se salta la comprobación de
 * permiso y sella fotos con coordenadas caducadas).
 */
const DEFAULT_MAXIMUM_AGE_MS = 0;

/**
 * Intenta obtener la posición actual. Resuelve a la coordenada o a `null`
 * (permiso denegado / sin soporte / timeout / error). Nunca rechaza.
 */
export function captureGeolocation(
  options: { timeoutMs?: number; maximumAgeMs?: number } = {},
): Promise<CapturedGeo | null> {
  const geo =
    typeof navigator !== "undefined" && "geolocation" in navigator
      ? navigator.geolocation
      : undefined;
  if (!geo) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const done = (value: CapturedGeo | null) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    try {
      geo.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          if (
            typeof latitude !== "number" ||
            typeof longitude !== "number" ||
            !Number.isFinite(latitude) ||
            !Number.isFinite(longitude)
          ) {
            done(null);
            return;
          }
          done({
            latitude,
            longitude,
            geoAccuracy:
              typeof accuracy === "number" && Number.isFinite(accuracy) && accuracy >= 0
                ? accuracy
                : null,
          });
        },
        () => {
          done(null);
        },
        {
          enableHighAccuracy: true,
          timeout: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
          maximumAge: options.maximumAgeMs ?? DEFAULT_MAXIMUM_AGE_MS,
        },
      );
    } catch {
      done(null);
    }
  });
}
